/**
 * LinkTargetSelect — универсальный селектор целевой ссылки платформы.
 *
 * Единый компонент для всех мест, где админ выбирает «куда ведёт ссылка»:
 * баннеры, мини-баннеры, UTM-источники (переход после открытия) и т.д.
 *
 * Типы цели:
 *  - страница платформы (статичные роуты из platform-routes.generated.ts —
 *    перегенерация: cd apps/admin && bun run gen:platform-routes);
 *  - сущность (курс / абонемент / канал-пасс / публикация / преподаватель) —
 *    динамическая загрузка через Refine useList;
 *  - своя ссылка (любой URL).
 *
 * Значение — единая строка linkUrl: `/courses/123`, `/dashboard`, `https://...`.
 * Совместимо с форматом баннеров (openSmartLink на платформе).
 */

import { useMemo, useState } from 'react';
import { useList } from '@refinedev/core';
import { ChevronsUpDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { FormField } from '@/components/forms';
import { cn } from '@/lib/utils';
import { PLATFORM_ROUTES } from './platform-routes.generated';

// ============================================
// Конфигурация типов цели
// ============================================

export const LINK_TARGET_OPTIONS = [
  { value: 'custom', label: 'Своя ссылка' },
  { value: 'page', label: 'Страница платформы' },
  { value: 'courses', label: 'Курс' },
  { value: 'subscription-plans', label: 'Абонемент' },
  { value: 'channel-passes', label: 'Канал-пасс' },
  { value: 'posts', label: 'Публикация' },
  { value: 'teachers', label: 'Преподаватель' },
  { value: 'livestreams', label: 'Трансляция' },
] as const;

export type LinkTargetType = typeof LINK_TARGET_OPTIONS[number]['value'];

/** Generic entity from any resource API */
export interface LinkableEntity {
  id: number | string;
  // courses, announcements: translations is array with { title, languageCode }
  // subscription-plans, channel-passes: translations is Record<langCode, { name }>
  translations?: Array<{ title?: string; languageCode?: string }> | Record<string, { name?: string }>;
  // fallbacks
  code?: string;
  slug?: string;
  name?: string;
  title?: string;
  content?: string;
  firstName?: string;
  lastName?: string;
}

export function getEntityDisplayName(resource: string, item: LinkableEntity): string {
  const { translations } = item;

  switch (resource) {
    case 'courses':
    case 'livestreams':
    case 'announcements': {
      if (Array.isArray(translations)) {
        const t = translations.find((tr) => tr.languageCode === 'ru') ?? translations[0];
        if (t?.title) {return t.title;}
      }
      return item.slug ?? item.title ?? `#${item.id}`;
    }
    case 'subscription-plans':
    case 'channel-passes': {
      if (translations && !Array.isArray(translations)) {
        const t = translations.ru ?? Object.values(translations)[0];
        if (t?.name) {return t.name;}
      }
      return item.code ?? item.name ?? `#${item.id}`;
    }
    case 'teachers': {
      const fullName = [item.firstName, item.lastName].filter(Boolean).join(' ');
      return fullName || item.name || `#${item.id}`;
    }
    case 'posts': {
      if (item.title) {return item.title;}
      if (item.content) {
        const text = item.content.replace(/<[^>]*>/g, '').trim();
        if (text) {return text.length > 60 ? `${text.slice(0, 60)}…` : text;}
      }
      return `#${item.id}`;
    }
    default:
      return item.name ?? item.title ?? item.slug ?? `#${item.id}`;
  }
}

// ============================================
// Парсинг / сборка linkUrl
// ============================================

export function parseLinkTarget(linkUrl: string | null): { targetType: LinkTargetType; entityId: string; customUrl: string; pagePath: string } {
  if (!linkUrl) {
    return { targetType: 'custom', entityId: '', customUrl: '', pagePath: '' };
  }

  // Internal entity links: /courses/123, /subscription-plans/5, …
  for (const opt of LINK_TARGET_OPTIONS) {
    if (opt.value === 'custom' || opt.value === 'page') {continue;}
    const prefix = `/${opt.value}/`;
    if (linkUrl.startsWith(prefix)) {
      return { targetType: opt.value, entityId: linkUrl.slice(prefix.length), customUrl: '', pagePath: '' };
    }
  }

  // Статичная страница платформы из манифеста
  if (PLATFORM_ROUTES.some((r) => r.path === linkUrl)) {
    return { targetType: 'page', entityId: '', customUrl: '', pagePath: linkUrl };
  }

  return { targetType: 'custom', entityId: '', customUrl: linkUrl, pagePath: '' };
}

export function buildLinkUrl(targetType: LinkTargetType, entityId: string, customUrl: string, pagePath: string): string | null {
  if (targetType === 'custom') {
    return customUrl || null;
  }
  if (targetType === 'page') {
    return pagePath || null;
  }
  if (!entityId) {return null;}
  return `/${targetType}/${entityId}`;
}

// ============================================
// LinkTargetSelect — единый контрол поверх value=linkUrl
// ============================================

interface LinkTargetSelectProps {
  value: string | null;
  onChange: (linkUrl: string | null) => void;
  /** Подпись селекта типа (по умолчанию «Тип ссылки») */
  typeLabel?: string;
}

export function LinkTargetSelect({ value, onChange, typeLabel = 'Тип ссылки' }: LinkTargetSelectProps) {
  const parsed = useMemo(() => parseLinkTarget(value), [value]);
  // Тип храним локально: переключение типа не должно затирать value у родителя,
  // пока не выбрана конкретная цель нового типа.
  const [targetType, setTargetType] = useState<LinkTargetType>(parsed.targetType);

  const handleTypeChange = (next: LinkTargetType) => {
    setTargetType(next);
    onChange(null);
  };

  return (
    <div className="space-y-4">
      <FormField label={typeLabel} name="linkTargetType">
        <Select value={targetType} onValueChange={(v) => handleTypeChange(v as LinkTargetType)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LINK_TARGET_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      {targetType === 'custom' && (
        <FormField label="URL" name="linkCustomUrl">
          <Input
            value={targetType === parsed.targetType ? parsed.customUrl : ''}
            onChange={(e) => onChange(e.target.value || null)}
            placeholder="https://..."
          />
        </FormField>
      )}

      {targetType === 'page' && (
        <PlatformPageSelector
          value={targetType === parsed.targetType ? parsed.pagePath : ''}
          onChange={(path) => onChange(path || null)}
        />
      )}

      {targetType !== 'custom' && targetType !== 'page' && (
        <EntitySelector
          resource={targetType}
          value={targetType === parsed.targetType ? parsed.entityId : ''}
          onChange={(id) => onChange(buildLinkUrl(targetType, id, '', ''))}
        />
      )}
    </div>
  );
}

// ============================================
// Селектор статичной страницы платформы
// ============================================

interface PlatformPageSelectorProps {
  value: string;
  onChange: (path: string) => void;
}

function PlatformPageSelector({ value, onChange }: PlatformPageSelectorProps) {
  const [open, setOpen] = useState(false);
  const selected = PLATFORM_ROUTES.find((r) => r.path === value);

  return (
    <FormField label="Страница" name="linkPagePath">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between font-normal h-auto min-h-10">
            <span className="truncate text-left">
              {selected ? `${selected.label} (${selected.path})` : 'Выберите страницу'}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" sideOffset={4}>
          <Command>
            <CommandInput placeholder="Поиск страницы..." />
            <CommandList>
              <CommandEmpty>Ничего не найдено</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__none__"
                  onSelect={() => {
                    onChange('');
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                  Не выбрано
                </CommandItem>
                {PLATFORM_ROUTES.map((route) => (
                  <CommandItem
                    key={route.path}
                    value={`${route.label} ${route.path}`}
                    onSelect={() => {
                      onChange(route.path);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn('mr-2 h-4 w-4', value === route.path ? 'opacity-100' : 'opacity-0')} />
                    <span className="flex flex-col">
                      <span>{route.label}</span>
                      <span className="text-xs text-muted-foreground">{route.path}</span>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </FormField>
  );
}

// ============================================
// Селектор сущности (курс / абонемент / …)
// ============================================

interface EntitySelectorSingleProps {
  resource: string;
  value: string;
  onChange: (id: string) => void;
  multiple?: false;
}

interface EntitySelectorMultiProps {
  resource: string;
  value: string[];
  onChange: (ids: string[]) => void;
  multiple: true;
}

type EntitySelectorProps = EntitySelectorSingleProps | EntitySelectorMultiProps;

export function EntitySelector(props: EntitySelectorProps) {
  const { resource, multiple } = props;
  const [open, setOpen] = useState(false);

  const { result } = useList<LinkableEntity>({
    resource,
    pagination: { pageSize: 100, mode: 'server' },
  });

  const items = result?.data;
  const label = LINK_TARGET_OPTIONS.find((o) => o.value === resource)?.label ?? 'Сущность';

  const options = useMemo(
    () => (items ?? []).map((item) => ({
      value: String(item.id),
      label: getEntityDisplayName(resource, item),
    })),
    [items, resource],
  );

  const isSelected = (optionValue: string) => {
    if (multiple) {
      return (props.value as string[]).includes(optionValue);
    }
    return (props.value as string) === optionValue;
  };

  const handleSelect = (optionValue: string) => {
    if (multiple) {
      const current = props.value as string[];
      const onChangeMulti = props.onChange as (ids: string[]) => void;
      if (current.includes(optionValue)) {
        onChangeMulti(current.filter((v) => v !== optionValue));
      } else {
        onChangeMulti([...current, optionValue]);
      }
    } else {
      (props.onChange as (id: string) => void)(optionValue);
      setOpen(false);
    }
  };

  const displayValue = () => {
    const placeholder = `Выберите ${label.toLowerCase()}`;
    if (multiple) {
      const selected = props.value as string[];
      if (selected.length === 0) {
        return placeholder;
      }
      const names = selected
        .map((v) => options.find((o) => o.value === v)?.label)
        .filter(Boolean);
      return names.join(', ') || `Выбрано: ${selected.length}`;
    }
    return options.find((o) => o.value === (props.value as string))?.label ?? placeholder;
  };

  return (
    <FormField label={label} name="linkEntityId">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between font-normal h-auto min-h-10"
          >
            <span className="truncate text-left">
              {displayValue()}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" sideOffset={4}>
          <Command>
            <CommandInput placeholder="Поиск..." />
            <CommandList>
              <CommandEmpty>Ничего не найдено</CommandEmpty>
              <CommandGroup>
                {!multiple && (
                  <CommandItem
                    value="__none__"
                    onSelect={() => {
                      (props.onChange as (id: string) => void)('');
                      setOpen(false);
                    }}
                  >
                    <Check className={cn('mr-2 h-4 w-4', !props.value ? 'opacity-100' : 'opacity-0')} />
                    Не выбрано
                  </CommandItem>
                )}
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => handleSelect(option.value)}
                  >
                    <Check className={cn('mr-2 h-4 w-4', isSelected(option.value) ? 'opacity-100' : 'opacity-0')} />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </FormField>
  );
}
